"use client"

import React, { useState, useContext, useEffect, FC } from "react"
import BackToButton from "../../../components/BackToButton"
import ICFSubscriptionCheckoutCard from "../_components/ICFSubscriptionCheckoutCard"
import Loader from "../../../components/ui/loader"
import useLoading from "../../../hooks/useLoading"
import useScrollToTop from "../../../hooks/useScrollToTop"
import { CommonFunctionCtx } from "../../../context/commonFunctionContext"
import { SubscriptionContext } from "../../../components/subscription/SubscriptionContext"
import { SubscriptionRequests, ReferralRequests } from "../../../api/app.service"
import {
  SvgCheckbox,
  SvgCheckboxOff,
  SvgLock2,
  SvgPromoCode,
  SvgZoeeIcon,
} from "../../../components/icons"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../../context/userContext"
import { ValidatePromoCodeResponseType } from "../../../models/app.interface"
import { useToast } from "../../../hooks/useToast"

export default function ICFUpgradeSubscription() {
  const navigate = useNavigate()
  const { user } = useContext(UserCtx)
  const [loadingReferralCode, setLoadingReferralCode] =
    useState<boolean>(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState<boolean>(false)
  const [isPromocode, setIsPromocode] = useState<boolean>(false)
  const [promoCode, setPromoCode] = useState<string>("ICFMEMBER2024")
  const [isAutopopulatedPromoCode, setIsAutopopulatedPromoCode] =
    useState<boolean>(true)
  const [appliedDiscount, setAppliedDiscount] = useState<any>({
    amount: 0,
    promoCode: "",
  })
  const { getUser } = useContext(CommonFunctionCtx)
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const {
    selectedPaymentMethod,
    subscription,
    selectedSubscription,
    setShowSubscriptionCheckout,
    isSubscriptionUpdate,
    setPaymentMethodModal,
    stripeRedirectParams,
    getSubscription,
  } = useContext(SubscriptionContext)

  const goBack = () => {
    navigate("")
  }

  const createSubscription = () => {
    SubscriptionRequests.createSubscription({
      user_id: parseInt(user.user_id),
      stripe_payment_method_id: selectedPaymentMethod.stripe_payment_method_id,
      stripe_price_id: selectedSubscription?.stripe_price_id,
      promotion_code:
        appliedDiscount.promoCode === "" ? null : appliedDiscount.promoCode,
    })
      .then((data) => {
        if (data.subscribed_with_referral) {
          ReferralRequests.updateReferralOnSubscribe({
            user_id: parseInt(user?.user_id),
            promo_code: promoCode,
            email: user?.email,
          }).catch((ex) => {
            console.error("updateReferralOnSubscribe err", ex)
          })
        }
      })
      .then(() => {
        setShowSubscriptionCheckout(false)
        getSubscription()
        toast.success(
          "Congratulations! You have purchased a coach subscription."
        )
        navigate("/coach")
      })
      .catch((ex) => {
        console.error(ex)
        const errorMessage =
          ex.response?.data?.error_detail ||
          ex.response?.data?.message ||
          "An unexpected error occurred."
        toast.error(errorMessage)
        stopLoading()
      })
  }

  const updateSubscription = () => {
    SubscriptionRequests.updateSubscription({
      user_id: parseInt(user.user_id),
      stripe_payment_method_id: selectedPaymentMethod.stripe_payment_method_id,
      current_stripe_price_id: subscription.stripe_price_id,
      new_stripe_price_id: selectedSubscription?.stripe_price_id,
      promotion_code:
        appliedDiscount.promoCode === "" ? null : appliedDiscount.promoCode,
    })
      .then(() => {
        if (appliedDiscount.promoCode) {
          ReferralRequests.updateReferralOnSubscribe({
            user_id: parseInt(user.user_id),
            promo_code: promoCode,
            email: user.email,
          }).catch((ex) => {
            console.error("updateReferralOnSubscribe err", ex)
          })
        }
      })
      .then(() => {
        toast.success("Subscription updated!")
        setShowSubscriptionCheckout(false)
        setTimeout(() => {
          getUser().then(() => {
            window.location.href = "/coach/subscription?updated=true"
          })
        }, 2500)
      })
      .catch((ex) => {
        console.error(ex)
        const errorMessage =
          ex.response?.data?.error_detail ||
          ex.response?.data?.message ||
          "An unexpected error occurred."
        toast.error(errorMessage)
        stopLoading()
      })
  }

  const submitSubscriptionPurchase = () => {
    if (isTermsAgreed && selectedPaymentMethod) {
      startLoading()
      if (isSubscriptionUpdate) {
        updateSubscription()
      } else {
        createSubscription()
      }
    } else {
      if (!isTermsAgreed) {
        toast.error("You must agree to the terms to complete your purchase")
      } else if (!selectedPaymentMethod) {
        toast.error("You must add a payment method to complete your purchase")
      } else {
        toast.error("Please agree to terms and add a payment method")
      }
    }
  }

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value)
  }

  const handlePromoResponse = (data: ValidatePromoCodeResponseType) => {
    if (!data.promo_valid) {
      toast.error(data.promo_invalid_reason)
      setAppliedDiscount({ amount: 0, promoCode: "" })
      setIsPromocode(false)
      setPromoCode("")
    } else {
      const details = data.promo_code
      const discountType = details.promo_value_type
      const discountUnits = details.promo_value
      const promoCode = details.promo_code
      if (discountType === "percent") {
        const discountDollars =
          (discountUnits / 100) * selectedSubscription?.amount
        setAppliedDiscount({
          amount: discountDollars,
          promoCode: promoCode,
        })
        toast.success(
          `Congrats! Your promotion code is saving you $${discountDollars.toFixed(
            2
          )} on this payment`
        )
      } else if (discountType === "amount") {
        setAppliedDiscount({
          amount: discountUnits,
          promoCode: promoCode,
        })
        toast.success(
          `Congrats! Your ${
            isAutopopulatedPromoCode ? "Zoee Referral Program" : ""
          } promotion code ${
            isAutopopulatedPromoCode ? `${promoCode}` : ""
          } is saving you $${discountUnits.toFixed(2)} on this payment`
        )
      }
      setIsPromocode(false)
    }
  }

  const validatePromoCode = () => {
    if (promoCode !== "") {
      setLoadingReferralCode(true)
      SubscriptionRequests.validatePromoCode({
        promo_code: promoCode,
        promo_duration_interval: selectedSubscription?.recurring_interval,
        user_id: parseInt(user.user_id),
      })
        .then((data) => {
          handlePromoResponse(data)
          setLoadingReferralCode(false)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          setLoadingReferralCode(false)
        })
    }
  }

  const getReferralPromoCode = () => {
    setLoadingReferralCode(true)
    ReferralRequests.getReferralPromoCode({
      user_id: parseInt(user.user_id),
    })
      .then((data) => {
        if (data.promo_code) {
          setIsPromocode(true)
          setPromoCode(data.promo_code)
          setIsAutopopulatedPromoCode(true)
        }
        setLoadingReferralCode(false)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
        setLoadingReferralCode(false)
      })
  }

  useEffect(() => {
    if (stripeRedirectParams) {
      setPaymentMethodModal(true)
    }
  }, [stripeRedirectParams])

  useEffect(() => {
    if (isAutopopulatedPromoCode) {
      validatePromoCode()
      setIsAutopopulatedPromoCode(false)
    }
  }, [isAutopopulatedPromoCode])

  useEffect(() => {
    if (promoCode === "") {
      validatePromoCode()
      setIsAutopopulatedPromoCode(true)
    }
  }, [promoCode])

  useScrollToTop()

  return (
    <div className="mt-4 mb-[32px]">
      <SvgZoeeIcon className="h-14 w-14 md:-mb-6" />
      <BackToButton
        onClick={goBack}
        current={isSubscriptionUpdate ? "My Subscription" : "Plans & Pricing"}
        prev={isSubscriptionUpdate ? "My Subscription" : "Plans & Pricing"}
      />
      <h1 className="font-bold flex text-[22px] md:text-[36px] text-center mb-[8px] -mt-4">
        Review Subscription
      </h1>
      <div className="flex flex-col md:flex-row items-center justify-center min-h-screen px-[20px]">
        <div className="flex flex-col items-center gap-[32px] w-full max-w-[784px] md:w-[784px] md:min-w-[700px]">
          <div className="mb-[32px] flex flex-col items-center gap-[32px] md:flex-row md:gap-[24px]">
            <div>
              <ICFSubscriptionCheckoutCard
                isNewPlan={true}
                subscription={selectedSubscription}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="border-grayCloud mt-[60px] flex flex-col border-0 md:mt-0 md:min-h-[856px] md:w-[50%] md:min-w-[416px] md:border-l md:pl-[80px]">
            <h3 className="mb-[32px] text-[22px] font-bold">Payment Details</h3>
            <h5 className="text-graySlate mb-[16px] text-base">
              PAYMENT SCHEDULE
            </h5>
            {isSubscriptionUpdate && (
              <p className="mb-[16px] text-base font-bold">
                The new subscription will start when the current period ends.
              </p>
            )}
            <div className="mb-[14px] flex items-center">
              {/* <SvgPiechartOneQuarter className="mr-[16px] h-6 w-6" /> */}
              {isSubscriptionUpdate ? (
                <p className="min-w-fit text-base">
                  1st payment on {/* mdDate not accepting useDate() */}
                  {/* {mdDate(subscription?.subscription_next_payment_date)} */}
                </p>
              ) : (
                <p className="min-w-fit text-base">1st payment today</p>
              )}
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              <p className="min-w-fit text-base">
                ${selectedSubscription?.amount?.toFixed(2)}
              </p>
            </div>
            <div className="mb-[12px] flex items-center">
              {/* <SvgRecurring className="mr-[16px] h-6 w-6 md:h-8 md:w-8" /> */}
              <p className="min-w-fit text-base">Recurring payments</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              <p className="min-w-fit text-base">
                $
                {selectedSubscription?.is_promotional
                  ? 0
                  : selectedSubscription?.amount?.toFixed(2)}
              </p>
            </div>

            {selectedSubscription.amount !== 500 && (
              <p className="text-graySlate mb-[24px] text-base">
                Payments will be charged
                {selectedSubscription?.recurring_interval === "annual" ? (
                  <span className="text-mainBlack font-semibold text-black">
                    {" "}
                    annually{" "}
                  </span>
                ) : (
                  <span className="text-mainBlack font-semibold text-black">
                    {" "}
                    monthly{" "}
                  </span>
                )}
                to your selected payment method.
              </p>
            )}
            <div className="bg-grayMist mb-[24px] h-[1px] w-full" />

            <div className="relative">
              <div className="mb-[16px] flex w-full items-center justify-between">
                <h5 className={`text-graySlate text-base`}>SUMMARY</h5>
                {loadingReferralCode ? (
                  <Loader />
                ) : (
                  <>
                    {isPromocode ? (
                      <button
                        onClick={() => setIsPromocode(() => !isPromocode)}
                        className="text-blurple text-base font-bold"
                      >
                        Cancel
                      </button>
                    ) : selectedSubscription?.amount === 500 ? (
                      ``
                    ) : (
                      <button
                        onClick={() => setIsPromocode(() => !isPromocode)}
                        className="text-blurple flex items-center gap-[8px] text-base font-bold"
                      >
                        <SvgPromoCode fill="#3395FF" />
                        Add Promocode
                      </button>
                    )}
                  </>
                )}
              </div>
              {isPromocode && (
                <div className="mb-[16px] flex items-center gap-[16px]">
                  <div className="default-input w-[222px]">
                    <input
                      type="text"
                      placeholder="Enter Promocode"
                      onChange={handlePromoCodeChange}
                      value={promoCode}
                    />
                    <div className="overlay">Promocode</div>
                  </div>
                  <button
                    className="btn-primary btn-blue h-[56px] w-[98px]"
                    onClick={validatePromoCode}
                  >
                    Save
                  </button>
                </div>
              )}
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">Interest or fees</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                <p className="min-w-fit text-base">$0.00</p>
              </div>
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">
                  {`Promotions ${
                    appliedDiscount?.promoCode &&
                    `(${appliedDiscount.promoCode})`
                  }`}
                </p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                <p className="min-w-fit text-base">
                  (${appliedDiscount.amount?.toFixed(2)})
                </p>
              </div>
            </div>

            <div className="mb-[24px] flex items-center">
              <p className="min-w-fit text-[18px] font-bold">Order total</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              <p className="min-w-fit text-[18px] font-bold">
                $
                {(
                  selectedSubscription?.amount?.toFixed(2) -
                  appliedDiscount.amount?.toFixed(2)
                ).toFixed(2)}
              </p>
            </div>
            <div className="bg-grayMist mb-[24px] h-[1px] w-full" />
            <div className="mb-[16px] flex items-center justify-between">
              <h5 className="text-graySlate text-base">PAYMENT METHOD</h5>
            </div>
            <div className="mb-[24px] flex gap-[16px]">
              <div>
                {isTermsAgreed ? (
                  <button
                    onClick={() => setIsTermsAgreed(() => !isTermsAgreed)}
                  >
                    <SvgCheckbox />
                  </button>
                ) : (
                  <button
                    onClick={() => setIsTermsAgreed(() => !isTermsAgreed)}
                  >
                    <SvgCheckboxOff stroke="#4750F5" strokeWidth="1" />
                  </button>
                )}
              </div>
              <div className="text-graySlate text-base">
                I have read and agree to the{" "}
                <a
                  href="https://zoee.com/terms-and-conditions"
                  className="text-blurple"
                  target="_blank"
                  rel="noreferrer"
                >
                  Agreement Disclosures
                </a>{" "}
                and {/* TODO add href */}
                <a href="#" className="text-blurple">
                  Payment Authorization
                </a>
                , and the
                {/* TODO add href */}
                <a href="#" className="text-blurple">
                  {" "}
                  refund policy{" "}
                </a>
                provided by Zoee
              </div>
            </div>

            <div className="mx-auto flex items-center gap-[8px] mt-2 mb-4">
              <SvgLock2 />
              <p className="text-gray text-base">
                Guaranteed safe and secure payments
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}